/** @jsxImportSource @emotion/react */
import { useEffect, useRef } from 'react'
import { Routes, Route, useParams, useLocation, useNavigate } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import moment from 'moment'

import HstVerify from './Verify'
import PatientDetails from './PatientDetails'
import PaymentRedirect from './PaymentRedirect'
import SignatureRequests from './SignatureRequests'
import { HstPaperworkStepper } from './ProgressBar'
import { getSteps } from './getSteps'
import { isDisqualifiedForHst, isInsuranceUnderReview } from './utils'
import {
  useLazyGetPatientOrderQuery,
  useStartPatientPaperworkMutation,
  useSavePatientPaperworkMutation,
  useFinishPatientPaperworkMutation,
  useCreatePatientCheckoutMutation,
} from 'store/services/patientEndpoints'
import { HstPatientPaperworkDto } from 'store/types'

const HstLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { orderId } = useParams()
  const showStepper = useMediaQuery('(min-width:600px)')
  const [getOrder, { currentData: order, data: test }] = useLazyGetPatientOrderQuery()
  const [startPatientPaperwork] = useStartPatientPaperworkMutation()
  const [savePatientPaperwork] = useSavePatientPaperworkMutation()
  const [finishPatientPaperwork] = useFinishPatientPaperworkMutation()
  const [createCheckout] = useCreatePatientCheckoutMutation()
  const initialRedirect = useRef(false)

  const paymentComplete = order?.state?.HST?.REQUIRED?.RESULT?.SCHEDULING?.PAYMENT === 'DONE'

  const steps = getSteps('OS')
  const activeStep = steps.find((step) => step.path && location.pathname.endsWith(step.path))

  const nextStep = () => navigate(steps[(activeStep?.idx || 0) + 1].path)

  useEffect(() => {
    if (orderId && localStorage.getItem('access_token')) {
      getOrder(Number(orderId))
    }
  }, [orderId, getOrder])

  useEffect(() => {
    if (order && !order.hst?.paperwork?.meta?.startedAt) {
      startPatientPaperwork(Number(order.id))
    }
    if (
      order &&
      !isDisqualifiedForHst(order) &&
      // The copay can change once the lab processes the corrected insurance, so don't open a
      // checkout session yet - the copay link is sent by SMS after the insurance is verified.
      !isInsuranceUnderReview(order) &&
      (!order.hst?.paymentSession ||
        moment().isAfter(moment(order.hst?.paymentSession?.expires_at * 1000)))
    ) {
      createCheckout({
        orderId: Number(order.id),
        amount: Number(order?.copay),
      })
    }
    if (order && !initialRedirect.current) {
      initialRedirect.current = true
      if (
        (paymentComplete || isDisqualifiedForHst(order)) &&
        order.hst?.paperwork?.meta?.finishedAt
      ) {
        navigate('/landing/paperwork/complete')
      } else if (order.hst?.paperwork?.meta?.finishedAt && isInsuranceUnderReview(order)) {
        // Paperwork is on hold until the lab has reviewed the insurance the patient corrected.
        navigate('/landing/paperwork/insurance-review')
      } else if (
        order.hst?.paperwork?.meta?.finishedAt &&
        !paymentComplete &&
        order.hst?.paymentSession?.url &&
        moment().isBefore(moment(order.hst?.paymentSession?.expires_at * 1000))
      ) {
        window.location.href = order.hst?.paymentSession?.url
      } else if (order.hst?.paperwork?.meta?.completedStep) {
        const lastCompletedStep = steps.findIndex(
          (step) => step.completedStep === order.hst?.paperwork?.meta?.completedStep,
        )
        navigate(steps[lastCompletedStep + 1].path)
      }
    }
  }, [order, startPatientPaperwork])

  const savePaperwork = async (data: HstPatientPaperworkDto) => {
    try {
      await savePatientPaperwork(data).unwrap()
    } catch (err) {
      console.log('err', err)
      if (err.status === 401) {
        localStorage.removeItem('access_token')
        navigate('/')
      }
      throw err
    }
  }

  const finishPaperwork = async (data) => {
    try {
      await finishPatientPaperwork(data).unwrap()
    } catch (err) {
      console.log('err', err)
      if (err.status === 401) {
        localStorage.removeItem('access_token')
        navigate('/')
      }
      throw err
    }
  }

  const props = {
    nextStep,
    order,
    title: 'Home Sleep Test (HST) - Patient Questionnaire and eSignatures',
    savePatientPaperwork: savePaperwork,
  }

  return (
    <div className="">
      {showStepper ? (
        <HstPaperworkStepper
          activeStep={activeStep?.idx || 0}
          steps={steps.map((step) => step.label)}
        />
      ) : (
        <div
          className="d-flex justify-content-center align-items-center w-100 text-success mb-3"
          css={{ fontSize: '1.3rem' }}>
          Step {(activeStep?.idx || 0) + 1} of {steps.length}
        </div>
      )}
      <Routes>
        <Route path={`patient-details`} element={<PatientDetails {...props} />} />
        <Route
          path={`signature-requests`}
          element={<SignatureRequests {...props} finishPaperwork={finishPaperwork} />}
        />
        {/* The resume logic can send a finished-paperwork patient here when the checkout session
            has expired and a replacement is still being created. */}
        <Route path={`stripe`} element={<PaymentRedirect order={order} />} />

        <Route path={``} element={<HstVerify {...props} getOrder={getOrder} />} />
      </Routes>
    </div>
  )
}

export default HstLayout
